import { motion } from "framer-motion";
import { TrendingUp, Landmark, Building, BadgeCheck, Check } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { PageHero } from "../components/pt/PageHero";
import { ClosingCTA } from "../components/pt/ClosingCTA";
import { Calculator } from "../components/pt/Calculator";
import { useDocumentTitle } from "../lib/useDocumentTitle";

const BENEFIT_ICONS = [TrendingUp, Landmark, Building, BadgeCheck];

const Banks = () => {
    const { t } = useLang();
    const p = t.banksPage;
    useDocumentTitle("/bancos");
    // piece 4 marker: top-right on
    const piecePattern = [
        ["", "on"],
        ["", ""],
        ["", "gap"],
    ];

    return (
        <div data-testid="banks-page">
            <PageHero
                audienceTag={p.audienceTag}
                title={p.title}
                lead={p.lead}
                piecePattern={piecePattern}
                testId="banks-hero"
            />

            {/* Benefits */}
            <section className="sec" data-testid="banks-benefits">
                <div className="wrap">
                    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                        {p.benefits.map((b, i) => {
                            const Icon = BENEFIT_ICONS[i] || Landmark;
                            return (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.5, delay: i * 0.08 }}
                                    className="group rounded-xl border border-linea bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(20,35,64,0.3)]"
                                    data-testid={`banks-benefit-${i}`}
                                >
                                    <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-lg bg-niebla text-coral-profundo transition-colors group-hover:bg-coral-profundo group-hover:text-white">
                                        <Icon size={20} />
                                    </div>
                                    <h3 className="h3 mb-2">{b.title}</h3>
                                    <p className="text-[0.98rem] text-acero">
                                        {b.body}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Compliance / Traceability */}
            <section className="sec mist" data-testid="banks-compliance">
                <div className="wrap grid gap-14 lg:grid-cols-2">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="h2 max-w-[18ch]">
                            {p.compliance.title}
                        </h2>
                        <p className="lead mt-4">{p.compliance.lead}</p>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                        className="space-y-3"
                    >
                        {p.compliance.items.map((item, i) => (
                            <details
                                key={i}
                                className="group rounded-xl border border-linea bg-white p-5 transition-colors hover:border-tinta/40"
                                open={i === 0}
                                data-testid={`banks-compliance-${i}`}
                            >
                                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[1.02rem] font-semibold text-tinta">
                                    <span className="flex items-center gap-3">
                                        <Check
                                            size={18}
                                            className="flex-none text-verde"
                                        />
                                        {item.h}
                                    </span>
                                    <span className="text-coral-profundo transition-transform group-open:rotate-45">
                                        +
                                    </span>
                                </summary>
                                <p className="mt-3 pl-8 text-[0.96rem] text-acero">
                                    {item.p}
                                </p>
                            </details>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Roadmap */}
            <section className="sec" data-testid="banks-roadmap">
                <div className="wrap">
                    <div className="mb-10 max-w-2xl">
                        <h2 className="h2">{p.roadmap.title}</h2>
                        <p className="lead">{p.roadmap.lead}</p>
                    </div>
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                        {p.roadmap.items.map((s, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                                className="relative rounded-xl border-t-2 border-coral-profundo bg-niebla p-6"
                                data-testid={`banks-step-${i}`}
                            >
                                <div
                                    className="mb-3 text-sm font-semibold text-coral-profundo tab-nums"
                                    style={{ fontStretch: "118%" }}
                                >
                                    {s.n}
                                </div>
                                <h3 className="h3 mb-2">{s.h}</h3>
                                <p className="text-[0.95rem] text-acero">
                                    {s.p}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Calculator */}
            <section id="calculadora" className="sec mist" data-testid="banks-calculator">
                <div className="wrap">
                    <div className="mb-10 max-w-3xl">
                        <div className="pt-eyebrow mb-4">
                            <span className="dot" />
                            <span>{t.calc.sponsor.hero.eyebrow}</span>
                        </div>
                        <h2 className="h2">{t.calc.sponsor.hero.title}</h2>
                        <p className="lead mt-4">
                            {t.calc.sponsor.hero.lead}
                        </p>
                    </div>
                    <Calculator type="sponsor" />
                </div>
            </section>

            <ClosingCTA testId="banks-closing" />
        </div>
    );
};

export default Banks;
